// src/rms/components/ErrorBoundary.js
// ============================================================================
// Top-level error boundary
// ============================================================================
// Replaces the inherited Sentry-wired boundary. Deliberately dependency-free:
// wire error reporting back in when there is a service to report to, rather
// than carrying the old integration forward for its own sake.
// ============================================================================

import React from "react";
import i18n from "../i18n";

/**
 * Translate without a hook.
 *
 * This is a class component, so useTranslation() is not available, and
 * withTranslation() would wrap the very boundary that has to survive a render
 * failure. Calling the i18next instance directly keeps the boundary
 * dependency-light and, crucially, working: i18next returns the fallback for a
 * missing key rather than throwing, so a broken catalogue cannot take out the
 * screen whose whole job is to show up when something else broke.
 */
const t = (key, fallback) => {
  try {
    return i18n.t(key, { defaultValue: fallback });
  } catch {
    return fallback;
  }
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("Unhandled error:", error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        style={{
          display: "grid", placeItems: "center", minHeight: "70vh", padding: 24,
          fontFamily: "system-ui, sans-serif", textAlign: "center", color: "#0f172a",
        }}
      >
        <div style={{ maxWidth: 460 }}>
          <h1 style={{ fontSize: 20, margin: "0 0 8px" }}>{t("errorBoundary.somethingWentWrong", "Something went wrong")}</h1>
          <p style={{ fontSize: 14, color: "#475569", margin: "0 0 20px" }}>
            {t("errorBoundary.body", "This screen hit an error. Your data is safe.")}
          </p>
          {import.meta.env.DEV ? (
            <pre
              style={{
                textAlign: "start", fontSize: 12, background: "#f8fafc", border: "1px solid #e2e8f0",
                borderRadius: 8, padding: 12, overflow: "auto", maxHeight: 220, marginBottom: 20,
              }}
            >
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          ) : null}
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              style={{ padding: "9px 18px", borderRadius: 9, border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer", fontSize: 14 }}
            >
              {t("errorBoundary.tryAgain", "Try again")}
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = "/"; }}
              style={{ padding: "9px 18px", borderRadius: 9, border: "none", background: "#08345b", color: "#fff", cursor: "pointer", fontSize: 14 }}
            >
              {t("errorBoundary.goHome", "Go home")}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
